import { LogOut, Coins, RefreshCw, ChevronDown } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppStore } from '@/stores/appStore';
import { getCreditBalances } from '@/services/credits';
import { useEffect, useState } from 'react';
import { FEATURE_FLAGS } from '@/services/api';

const HEADER_LOGO_URL = 'https://assets.cdn.filesafe.space/ceLRhUdWIdfZ8UcJopVc/media/69c04e36ad1400a8b2f1d749.png';

function CreditHint({ label, hint, align = 'left' }: { label: string; hint: string; align?: 'left' | 'right' }) {
  const tooltipPosition = align === 'right' ? 'right-0' : 'left-0';
  return (
    <span className="relative inline-flex items-center gap-1 group">
      <span className="text-gray-300">{label}</span>
      <span
        tabIndex={0}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-gray-500 text-[10px] text-gray-300 cursor-help"
        aria-label={hint}
      >
        i
      </span>
      <span
        className={`pointer-events-none absolute ${tooltipPosition} top-full z-50 mt-1 hidden rounded-md border border-gray-700 bg-gray-900 px-2 py-1 text-xs text-gray-200 shadow-xl group-hover:block group-focus-within:block`}
        style={{ width: '340px', maxWidth: 'calc(100vw - 2rem)' }}
      >
        {hint}
      </span>
    </span>
  );
}

export function Header() {
  const {
    userEmail,
    credits,
    setCredits,
    setContactCredits,
    logout,
    isAdmin,
    accessCustomKpi,
    accessContentScraping,
    accessProfitPod,
    accessEmailWorkspace,
  } = useAppStore();
  const [refreshing, setRefreshing] = useState(false);
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    if (typeof window === 'undefined') return 'dark';
    const stored = window.localStorage.getItem('solo-theme');
    return stored === 'light' ? 'light' : 'dark';
  });
  const navigate = useNavigate();
  const location = useLocation();
  const [contentOpen, setContentOpen] = useState(false);
  const [profitPodOpen, setProfitPodOpen] = useState(false);
  const [listsOpen, setListsOpen] = useState(false);
  const [searchesOpen, setSearchesOpen] = useState(false);
  const [listenOpen, setListenOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const normalizedEmail = userEmail.trim().toLowerCase();
  const isProfitibullDomain = /@(profitibull|profitibill)\.com$/.test(normalizedEmail);
  const canAccessCommandCenter = isAdmin || isProfitibullDomain;
  const dailyContactDisplay = isProfitibullDomain ? 'Unlimited' : '200';
  const monthlyScrapeDisplay = isProfitibullDomain ? 'Unlimited' : String(credits);

  const handleRefreshCredits = async () => {
    if (!userEmail || refreshing) return;
    setRefreshing(true);
    try {
      const balances = await getCreditBalances(userEmail);
      setCredits(balances.credits);
      setContactCredits(balances.contactCredits);
    } catch (error) {
      console.error('Failed to refresh credits:', error);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!userEmail) return;
    void handleRefreshCredits();
    // userEmail scope is intentional; this should run once per authenticated user context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;


  const isListsActive = [
    '/my-lists',
    '/my-lists/contacts',
    '/my-lists/companies',
    '/my-lists/podcasts',
    '/my-lists/hot',
  ].includes(location.pathname);

  const isContentActive = [
    '/ad-data',
    '/social-listener',
    '/trending-keyword-research',
    '/group-scraping',
    '/contact-socials',
  ].includes(location.pathname);

  const isProfitPodActive = [
    '/podcasts',
    '/booking-ai-agent',
  ].includes(location.pathname);

  const isSearchesActive = [
    '/search',
    '/lead-search',
    '/company-search',
    '/custom-kpi-search',
    '/scrappy',
  ].includes(location.pathname);

  const isListenActive = ['/listening-linkedin', '/listening-facebook'].includes(location.pathname);
  const isEmailActive = ['/email/data', '/email/campaign-builder', '/email/campaign-editor', '/email/email-builder'].includes(location.pathname);
  const isPepActive = location.pathname === '/ai-engagement-protocol';

  const supportsLightMode = [
    '/search',
    '/lead-search',
    '/lead-research',
    '/company-search',
    '/scrappy',
    '/my-lists',
    '/my-lists/contacts',
    '/my-lists/companies',
    '/my-lists/podcasts',
    '/my-lists/hot',
    '/command-center',
    '/account',
    '/email/data',
    '/email/campaign-builder',
    '/email/campaign-editor',
    '/email/email-builder',
  ].includes(location.pathname);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('solo-theme', theme);
    const shouldApply = theme === 'light';
    document.body.classList.toggle('theme-light', shouldApply);
  }, [theme, supportsLightMode]);

  return (
    <nav className="bg-black border-b border-gray-800">
      {import.meta.env.DEV && (
        <div className="px-6 py-2 border-b border-gray-900 text-xs text-gray-300 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="uppercase tracking-wide text-gray-500">DEV</span>
            <span className="text-gray-400">Server-side credits:</span>
            <span className={FEATURE_FLAGS.SERVER_SIDE_CREDITS ? 'text-green-400 font-semibold' : 'text-red-400 font-semibold'}>
              {FEATURE_FLAGS.SERVER_SIDE_CREDITS ? 'ON' : 'OFF'}
            </span>
          </div>
          <div className="text-gray-500">
            {FEATURE_FLAGS.SERVER_SIDE_CREDITS ? 'Client deduct disabled (SOLO-safe)' : 'Client deduct enabled (prod behavior)'}
          </div>
        </div>
      )}
      <div className="max-w-full mx-auto flex items-center justify-between px-6 py-4">
        <div className="flex items-center space-x-8">
          <button onClick={() => navigate('/home')} className="flex items-center" aria-label="Simply Scrapable Home">
            <img
              src={HEADER_LOGO_URL}
              alt="Simply Scrapable"
              className="h-[144px] w-auto object-contain -my-[40px]"
            />
          </button>
          <div className="flex space-x-6 text-sm">
            <div className="relative">
              <button
                id="nav-search"
                onClick={() => setSearchesOpen((v) => !v)}
                className={isSearchesActive ? 'text-white font-semibold flex items-center gap-1' : 'text-gray-300 hover:text-white flex items-center gap-1'}
              >
                Search <ChevronDown className="w-3 h-3" />
              </button>
              {searchesOpen && (
                <div className="absolute left-0 mt-2 w-56 rounded-lg border border-gray-800 bg-black shadow-xl z-50">
                  {[
                    { path: '/lead-search', label: 'Contact Search', id: 'nav-search-contact' },
                    { path: '/company-search', label: 'Company Search', id: 'nav-search-company' },
                    { path: '/scrappy', label: 'Scrappy Guided Search', id: 'nav-search-scrappy' },
                    { path: '/custom-kpi-search', label: 'Custom KPI Search', id: 'nav-search-kpi', disabled: !accessCustomKpi },
                  ].map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        if (item.disabled) return;
                        setSearchesOpen(false);
                        navigate(item.path);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm ${
                        isActive(item.path) ? 'text-white bg-gray-800' : 'text-gray-300 hover:text-white hover:bg-gray-800'
                      } ${item.disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {accessContentScraping && (
              <div className="relative">
                <button
                  id="nav-research"
                  onClick={() => setContentOpen((v) => !v)}
                  className={isContentActive ? 'text-white font-semibold flex items-center gap-1' : 'text-gray-300 hover:text-white flex items-center gap-1'}
                >
                  Research <ChevronDown className="w-3 h-3" />
                </button>
                {contentOpen && (
                  <div className="absolute left-0 mt-2 w-64 rounded-lg border border-gray-800 bg-black shadow-xl z-50">
                    {[
                      { path: '/trending-keyword-research', label: 'Trending Keyword Analysis', id: 'nav-content-trending' },
                      { path: '/group-scraping', label: 'Group Search/Scraping', id: 'nav-content-groups' },
                      { path: '/ad-data', label: 'PPC and Paid Search Advertising', id: 'nav-content-ads' },
                    ].map((item) => (
                      <button
                        key={item.id}
                        onClick={() => {
                          setContentOpen(false);
                          navigate(item.path);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm ${
                          isActive(item.path) ? 'text-white bg-gray-800' : 'text-gray-300 hover:text-white hover:bg-gray-800'
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {(accessContentScraping || accessProfitPod) && (
              <div className="relative">
                <button
                  id="nav-scrape"
                  onClick={() => setProfitPodOpen((v) => !v)}
                  className={isProfitPodActive ? 'text-white font-semibold flex items-center gap-1' : 'text-gray-300 hover:text-white flex items-center gap-1'}
                >
                  Scrape <ChevronDown className="w-3 h-3" />
                </button>
                {profitPodOpen && (
                  <div className="absolute left-0 mt-2 w-56 rounded-lg border border-gray-800 bg-black shadow-xl z-50">
                    {[
                      { path: '/social-listener', label: 'Social Media Listener', id: 'nav-listening-sml' },
                      { path: '/podcasts', label: 'Podcast Finder', id: 'nav-profitpod-rephonic' },
                      { path: '/podcasts', label: 'Episode Scraping', id: 'nav-profitpod-scrape' },
                      { path: '/podcasts', label: 'iTunes Scrape', id: 'nav-profitpod-itunes' },
                      { path: '/podcasts', label: 'Spotify Scrape', id: 'nav-profitpod-spotify' },
                      { path: '/podcasts', label: 'Substack Scrape', id: 'nav-profitpod-substack' },
                    ].map((item) => (
                      <button
                        key={item.id}
                        onClick={() => {
                          setProfitPodOpen(false);
                          navigate(item.path);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm ${
                          isActive(item.path) ? 'text-white bg-gray-800' : 'text-gray-300 hover:text-white hover:bg-gray-800'
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="relative">
              <button
                id="nav-listen"
                onClick={() => setListenOpen((v) => !v)}
                className={isListenActive ? 'text-white font-semibold flex items-center gap-1' : 'text-gray-300 hover:text-white flex items-center gap-1'}
              >
                Listen <ChevronDown className="w-3 h-3" />
              </button>
              {listenOpen && (
                <div className="absolute left-0 mt-2 w-56 rounded-lg border border-gray-800 bg-black shadow-xl z-50">
                  {[
                    { path: '/listening-linkedin', label: 'LinkedIn Lead Generation', id: 'nav-listen-linkedin' },
                    { path: '/listening-facebook', label: 'Facebook Generation', id: 'nav-listen-facebook' },
                  ].map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        setListenOpen(false);
                        navigate(item.path);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-800"
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {(accessEmailWorkspace || isAdmin) && (
              <div className="relative">
                <button
                  id="nav-email"
                  onClick={() => setEmailOpen((v) => !v)}
                  className={isEmailActive ? 'text-white font-semibold flex items-center gap-1' : 'text-gray-300 hover:text-white flex items-center gap-1'}
                >
                  Email Outreach <ChevronDown className="w-3 h-3" />
                </button>
                {emailOpen && (
                  <div className="absolute left-0 mt-2 w-56 rounded-lg border border-gray-800 bg-black shadow-xl z-50">
                    {[
                      { path: '/email/data', label: 'Email Analytics', id: 'nav-email-data' },
                      { path: '/email/campaign-builder', label: 'Email Campaign Builder', id: 'nav-email-campaign-builder' },
                      { path: '/email/campaign-editor', label: 'Campaign Editor', id: 'nav-email-campaign-editor' },
                    ].map((item) => (
                      <button
                        key={item.id}
                        onClick={() => {
                          setEmailOpen(false);
                          navigate(item.path);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm ${
                          isActive(item.path) ? 'text-white bg-gray-800' : 'text-gray-300 hover:text-white hover:bg-gray-800'
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="relative">
              <button
                id="nav-lists"
                onClick={() => setListsOpen((v) => !v)}
                className={isListsActive ? 'text-white font-semibold flex items-center gap-1' : 'text-gray-300 hover:text-white flex items-center gap-1'}
              >
                My Data <ChevronDown className="w-3 h-3" />
              </button>
              {listsOpen && (
                <div className="absolute left-0 mt-2 w-48 rounded-lg border border-gray-800 bg-black shadow-xl z-50">
                  {[
                    { path: '/my-lists/contacts', label: 'Contacts', id: 'nav-lists-contacts' },
                    { path: '/my-lists/companies', label: 'Companies', id: 'nav-lists-companies' },
                    { path: '/my-lists/podcasts', label: 'Podcasts', id: 'nav-lists-podcasts' },
                    { path: '/my-lists/hot', label: 'Hot List', id: 'nav-lists-hot' },
                  ].map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        setListsOpen(false);
                        navigate(item.path);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm ${
                        isActive(item.path) ? 'text-white bg-gray-800' : 'text-gray-300 hover:text-white hover:bg-gray-800'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              id="nav-pep"
              onClick={() => navigate('/ai-engagement-protocol')}
              className={isPepActive ? 'text-white font-semibold' : 'text-gray-300 hover:text-white'}
            >
              P.E.P.ai
            </button>
            {canAccessCommandCenter && (
              <button
                id="nav-command-center"
                onClick={() => navigate('/command-center')}
                className={isActive('/command-center') ? 'text-white font-semibold' : 'text-gray-300 hover:text-white'}
              >
                Command Center
              </button>
            )}
            <button
              id="nav-account"
              onClick={() => navigate('/account')}
              className={isActive('/account') ? 'text-white font-semibold' : 'text-gray-300 hover:text-white'}
            >
              Account
            </button>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <div className="relative mr-6">
            <div className="bg-gray-800 rounded-lg px-4 py-2 min-w-[430px]">
              <div className="flex items-center gap-2 text-sm">
                <Coins className="w-4 h-4 text-yellow-500" />
                <CreditHint
                  label="Daily Contacts:"
                  hint="Daily Contacts: credits daily for contact/lead data"
                />
                <span className="text-white font-semibold">{dailyContactDisplay}</span>
                <span className="text-gray-600">|</span>
                <CreditHint
                  label="Monthly Scrapes:"
                  hint="Monthly Scrapes: Deep dives into social media, website, or other detailed scrapes"
                  align="right"
                />
                <span id="creditCount" className="text-white font-semibold">{monthlyScrapeDisplay}</span>
                {!isProfitibullDomain && (
                  <span className="text-gray-500 text-xs">(resets daily, no carryover)</span>
                )}
              </div>
              <div className="mt-1 flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span id="currentUserEmail" className="text-gray-300">{userEmail}</span>
                  <button
                    onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                    className="text-gray-400 hover:text-white p-1"
                    title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                  >
                    {theme === 'dark' ? '☀' : '☽'}
                  </button>
                  <button
                    onClick={handleLogout}
                    className="text-gray-400 hover:text-white p-1"
                    title="Logout"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate('/checkout')}
                    className="text-gray-200 hover:text-white text-sm font-semibold"
                  >
                    + Add Credits
                  </button>
                </div>
              </div>
              <button
                onClick={handleRefreshCredits}
                disabled={refreshing}
                className="absolute right-2 top-2 text-gray-400 hover:text-white disabled:opacity-50"
              >
                <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
